/**
 * Flattens a nested object into a single level object with dot notation for keys
 * Arrays are converted to strings for ClickHouse compatibility
 * 
 * @param obj - The object to flatten
 * @param prefix - The prefix to use for nested keys (used recursively)
 * @returns Flattened object
 */
function flattenObject(obj: Record<string, unknown>, prefix = ''): Record<string, string | number | boolean> {
  const flattened: Record<string, string | number | boolean> = {};

  for (const key in obj) {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (value === null) {
      flattened[newKey] = '';
    } else if (Array.isArray(value)) {
      // Convert arrays to JSON strings for ClickHouse compatibility
      flattened[newKey] = JSON.stringify(value);
    } else if (typeof value === 'object' && value !== null) {
      // Recursively flatten nested objects
      Object.assign(flattened, flattenObject(value as Record<string, unknown>, newKey));
    } else {
      // Keep primitive values as is, but ensure they're a valid type
      flattened[newKey] = (value === undefined) ? '' : (value as string | number | boolean);
    }
  }

  return flattened;
}

/**
 * Checks if a string appears to be in JSONL format
 * by verifying each non-empty line is a valid JSON object
 */
function isJSONL(text: string): boolean {
  const lines = text.split('\n').filter(line => line.trim().length > 0);
  try {
    return lines.every(line => {
      const parsed = JSON.parse(line);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
    });
  } catch {
    return false;
  }
}

/**
 * Converts various JSON formats to flattened JSONL format for ClickHouse.
 * Handles:
 * - Single objects
 * - Arrays of objects
 * - Nested objects (flattens with dot notation)
 * - Arrays (converts to JSON strings)
 * - Already JSONL formatted data (flattens if needed)
 * 
 * @param jsonData - The JSON data as a string or parsed object
 * @returns JSONL formatted string with flattened objects
 * @throws Error if input is invalid or cannot be processed
 */
export function convertToJSONL(jsonData: string | object): string {
  // If string input, check if it's already JSONL
  if (typeof jsonData === 'string') {
    if (isJSONL(jsonData)) {
      // Already JSONL, but still need to flatten each object
      const lines = jsonData.split('\n').filter(line => line.trim().length > 0);
      return lines
        .map(line => JSON.stringify(flattenObject(JSON.parse(line))))
        .join('\n');
    }
    // Not JSONL, parse it as JSON
    jsonData = JSON.parse(jsonData);
  }

  // Convert to array of objects
  const objects = Array.isArray(jsonData) ? jsonData : [jsonData];

  // Process each object and convert to JSONL
  return objects
    .map(obj => {
      if (typeof obj !== 'object' || obj === null) {
        throw new Error('Each item must be an object');
      }
      // Flatten the object and convert to JSON line
      return JSON.stringify(flattenObject(obj));
    })
    .join('\n');
} 